import { useEffect, useState, useCallback } from "react";
import { DeleteOutlined, EditOutlined, ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Modal, Space, Switch, Row, Col, Button, Table } from "antd";
import AddUser from "../components/users/AddUser";
import { deleteData, getData, updateData } from "../helpers/api";
import withRoleProtection from "../components/withRoleProtection";
import { openNotification } from "../helpers/alerts";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextInput from "../components/inputs/TextInput";
import SelectInput from "../components/inputs/SelectInput";
import { CiSearch } from "react-icons/ci";
import { RxReset } from "react-icons/rx";

const { confirm } = Modal;

const filterSchema = Yup.object({
    name: Yup.string().nullable().optional(),
    email: Yup.string().nullable().optional(),
    phone: Yup.string().nullable().optional(),
    prefix: Yup.string().nullable().optional(),
    role: Yup.string().nullable().optional(),
});

const INITIAL_FILTER = {
    name: "",
    email: "",
    phone: "",
    prefix: "",
    role: null,
};

const INITIAL_VALUES = {
    name: "",
    email: "",
    phone: "",
    city: undefined,
    prefix: "",
    password: "",
    role: null,
    paymentPerOrder: null,
    paymentPerUnitInOrder: null,
    paymentPerMoreThenOneProductInOrder: null,
    salaryPerMonth: null,
};

const Users = () => {
    const [user, setUser] = useState(INITIAL_VALUES);
    const [users, setUsers] = useState([]);
    const [filter, setFilter] = useState({});
    const [totalUsers, setTotalUsers] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const roleOptions = [
        { value: "super admin", label: "Super Admin" },
        { value: "Confirmation messenger", label: "Confirmation Messenger" },
        { value: "Confirmation whatsApp", label: "Confirmation WhatsApp" },
        { value: "Confirmation instagram", label: "Confirmation Instagram" },
        { value: "Confirmation convertion", label: "Confirmation Convertion" },
        { value: "Gestion", label: "Gestion" },
        { value: "Suivie", label: "Suivie" },
        { value: "Livreur", label: "Livreur" },
        { value: "statistique", label: "Statistique" },
    ];

    const showDeleteConfirm = (userId) => {
        confirm({
            title: "Vous êtes sûr de supprimer cet utilisateur ?",
            icon: <ExclamationCircleFilled />,
            okText: "Suprimer",
            okType: "danger",
            cancelText: "Anuler",
            onOk() {
                deleteUser(userId);
            },
        });
    };

    const showDeactiveConfirm = (userId, isActive) => {
        confirm({
            title: `Vous êtes sûr de ${isActive ? "desactiver" : "activer"} cet utilisateur ?`,
            icon: <ExclamationCircleFilled />,
            okText: isActive ? "Dasactiver" : "Activer",
            okType: "danger",
            cancelText: "Anuler",
            onOk() {
                deactivateUser(userId, isActive);
            },
        });
    };

    const deactivateUser = async (userId, isActive) => {
        try {
            await updateData("users/user/" + userId);
            openNotification(`L'utilisateur a été ${isActive ? "desactiver" : "activer"} avec succès`, "success");
            getUsers();
        } catch (error) {
            openNotification(`Erreurs côté serveur`, "error");
        }
    };

    const deleteUser = async (userId) => {
        try {
            await deleteData(`/users/${userId}`);
            openNotification(`L'utilisateur a été suprimer avec succès`, "success");
            getUsers();
        } catch (error) {
            openNotification(`Erreurs côté serveur`, "error");
        }
    };

    const columns = [
        {
            title: "Nom",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "E-mail",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Téléphone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Ville",
            dataIndex: "city",
            key: "city",
        },
        {
            title: "Prefix",
            dataIndex: "prefix",
            key: "prefix",
        },
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (_, record) => {
                return (
                    <Space size="middle">
                        <span>
                            <EditOutlined onClick={() => setUser({ ...record, edit: true })} />
                        </span>
                        <span>
                            <DeleteOutlined onClick={() => showDeleteConfirm(record.id)} />
                        </span>
                        <span>
                            <Switch
                                size="small"
                                defaultChecked={record.isActive}
                                onChange={() => showDeactiveConfirm(record.id, record.isActive)}
                            />
                        </span>
                    </Space>
                );
            },
        },
    ];

    const handleCancel = () => {
        setUser({ ...INITIAL_VALUES });
    };

    const handleAfterAddingUser = () => {
        getUsers();
        handleCancel();
    };

    const handleFilterSubmit = (values) => {
        setCurrentPage(1); // Reset to first page when filtering
        setFilter(values);
    };

    const getUsers = useCallback(async () => {
        try {
            const params = {
                ...Object.entries(filter)
                    .filter(([_, value]) => value !== null && value !== "")
                    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
                page: currentPage,
                limit: pageSize,
            };

            const res = await getData("/users", params);
            setUsers(res.data.users);
            setTotalUsers(res.data.total);
        } catch (error) {
            openNotification("Erreur lors de la récupération des utilisateurs", "error");
        }
    }, [filter, currentPage, pageSize]);

    useEffect(() => {
        getUsers();
    }, [filter, currentPage, pageSize, getUsers]);

    return (
        <div style={{ padding: "24px" }}>
            <div
                style={{
                    marginBottom: "24px",
                    background: "white",
                    padding: "24px",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
                }}
            >
                <Formik initialValues={INITIAL_FILTER} validationSchema={filterSchema} onSubmit={handleFilterSubmit}>
                    {({ resetForm }) => (
                        <Form>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <TextInput name="name" placeholder="Rechercher par nom" style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <TextInput name="email" placeholder="Rechercher par email" style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <TextInput name="phone" placeholder="Rechercher par téléphone" style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <TextInput name="prefix" placeholder="Rechercher par prefix" style={{ width: "100%" }} />
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <SelectInput
                                        name="role"
                                        placeholder="Filtrer par rôle"
                                        options={roleOptions}
                                        style={{ width: "100%" }}
                                    />
                                </Col>
                                <Col xs={24} sm={24} md={8} lg={6} style={{ display: "flex", gap: "8px" }}>
                                    <Button type="primary" htmlType="submit" icon={<CiSearch />}>
                                        Rechercher
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            resetForm();
                                            setCurrentPage(1); // Reset to first page when clearing filters
                                            setFilter({});
                                        }}
                                        icon={<RxReset />}
                                    >
                                        Réinitialiser
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </div>

            <div
                style={{
                    background: "white",
                    padding: "24px",
                    borderRadius: "8px",
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        textAlign: "center",
                        justifyContent: "flex-end",
                        padding: "10px",
                    }}
                >
                    <Button
                        onClick={() =>
                            setUser({
                                ...INITIAL_VALUES,
                                add: true,
                            })
                        }
                    >
                        <PlusOutlined
                            style={{
                                fontSize: "20px",
                                cursor: "pointer",
                            }}
                        />
                    </Button>
                </div>

                {(user.add || user.edit) && (
                    <AddUser
                        handleAfterAddingUser={handleAfterAddingUser}
                        initialValue={user}
                        isModalOpen={user.add || user.edit}
                        handleCancel={handleCancel}
                    />
                )}

                <div style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
                    <Table
                        columns={columns}
                        dataSource={users}
                        rowKey="id"
                        pagination={{
                            total: totalUsers,
                            current: currentPage,
                            pageSize: pageSize,
                            onChange: (page, pageSize) => {
                                setCurrentPage(page);
                                setPageSize(pageSize);
                            },
                        }}
                        locale={{
                            emptyText: "Aucune donnée trouvée",
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default withRoleProtection(Users, ["super admin"]);